import { useFormApi } from 'informed';
import { useDispatch, useReduxState } from 'hooks';
import { FormGroup } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';

import type { CourseFileUploader } from './CoursesCreateFromFile';

// Same limit as the import route and nginx. Checked here too, because
// nginx answers a larger upload with an HTML page the UI can't read.
const MOODLE_BACKUP_MAX_BYTES = 1000 * 1024 * 1024;

type Props = {
  onClose: () => void;
  onHasFileChange: (hasFile: boolean) => void;
  // the upload can't be resumed, so the modal must stay open while it runs
  onUploadRunningChange: (isUploadRunning: boolean) => void;
  uploaderRef: React.RefObject<CourseFileUploader>;
};

const CoursesCreateMoodle = ({
  onClose,
  onHasFileChange,
  onUploadRunningChange,
  uploaderRef
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const formApi = useFormApi();

  // the title is checked again by the route, which answers with the same codes
  const titleErrors: Record<string, string> = {
    title_is_not_unique: messages.coursesTitleInUse,
    title_is_required: messages.coursesTitleEmpty,
    title_is_too_long: messages.commonTextTooLong
  };

  const otherErrors: Record<string, string> = {
    moodle_import_background_jobs_unavailable:
      messages.coursesImportMoodleUnavailable,
    moodle_import_file_too_large: messages.coursesImportMoodleFileTooLarge,
    moodle_import_invalid_file_type: messages.coursesImportMoodleFileType
  };

  const handleUploadError = ({
    response
  }: {
    response?: { error?: string };
  }) => {
    onUploadRunningChange(false);

    const titleError = titleErrors[response?.error];
    if (titleError) {
      formApi.setError('title', titleError);
      return;
    }

    uploaderRef.current?.reset();
    dispatch.handleErrorDispatch(
      null,
      otherErrors[response?.error] || messages.commonUploadFileFailed
    );
  };

  const handleUploadSuccess = ({
    response
  }: {
    response: { jobId: number };
  }) => {
    dispatch.registerPendingJob({
      jobId: response.jobId,
      type: 'moodle-import'
    });
    dispatch.addNotice({
      text: messages.coursesImportMoodleStarted,
      type: 'success'
    });
    onUploadRunningChange(false);
    onClose();
  };

  return (
    <FormGroup label={messages.coursesImportMoodleFile}>
      <FileUploader
        allowedFileTypes={['.mbz']}
        destination="local"
        dragDrop={true}
        dragDropActions={false}
        endpoint="/admin/courses/import-moodle"
        fileSizeErrorText={messages.coursesImportMoodleFileTooLarge}
        fileTypeErrorText={messages.coursesImportMoodleFileType}
        getUploader={(instance) => (uploaderRef.current = instance)}
        icon="file"
        id="moodle-import-uploader"
        maxFileSize={MOODLE_BACKUP_MAX_BYTES}
        onError={handleUploadError}
        onFileAdded={() => onHasFileChange(true)}
        onFileRemoved={() => onHasFileChange(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </FormGroup>
  );
};

export default CoursesCreateMoodle;
